import { Link, Typography } from "@mui/material";
import { GridRenderCellParams, GridSortModel } from "@mui/x-data-grid-premium";
import {
  documentKindLabel,
  manifestNumberLabel,
} from "encamp-shared/src/waste/shippingDocumentLabels";
import { ShippingPaperworkRowFragment } from "generated-graphql/graphql";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { Link as RouterLink } from "react-router-dom";
import { parseOmnisearchTokens } from "utils/omnisearchUtils";

import type { Row } from "./Manifests";

import { MISSING_SHIP_DATE_LABEL, NO_VALUE_PLACEHOLDER } from "./shippingDocumentDisplay";
import { shippingPaperworkKindValues } from "./shippingPaperworkKindFilter";

/** One item straight off the `shippingPaperwork` query, before flattening. */
export type ShippingPaperworkItem = ShippingPaperworkRowFragment;

/**
 * A grid row for the merged Manifests list.
 *
 * V2's column definitions read manifest fields off the row itself
 * (`row.epaId`, `row.discrepancyTally`, …), so the manifest side is spread onto
 * the item before the row reaches the grid. That is what lets the merged list
 * render V2's `useManifestColumns` output unmodified instead of a parallel
 * column set. Being a subtype of `Row`, it is also what the guards below can
 * narrow a plain `Row` to.
 */
export type ShippingPaperworkRow = Row & Omit<ShippingPaperworkItem, "__typename">;

/** A row whose manifest side is populated. */
export type ManifestRow = ShippingPaperworkRow & {
  manifest: NonNullable<ShippingPaperworkItem["manifest"]>;
};

/**
 * Whether this row came from the merged list at all. V2's own Manifests grid
 * hands its columns a plain `Row` with no paperwork fields, so the narrowing
 * starts from the key only the merged list's rows carry.
 */
export function isPaperworkRow(row: Row): row is ShippingPaperworkRow {
  return "paperworkKind" in row;
}

export function isManifestRow(row: Row): row is ManifestRow {
  return "manifest" in row && row.manifest != null;
}

/**
 * The Type column's label, from the processed manifest's `documentKind` (a
 * bill of lading reads "Bill of Lading" and opens the same manifest drawer).
 * `paperworkKind` on the item carries the same value, and the `kind:` filter
 * matches on it, so the filter and this label agree.
 */
export function paperworkTypeLabel(row: Row): string {
  return isManifestRow(row) ? documentKindLabel(row.paperworkKind) : "";
}

/**
 * An all-zero tally and an empty source list, so a row with no manifest can
 * satisfy the two non-nullable `Row` fields it has no manifest to supply.
 */
const EMPTY_DISCREPANCY_TALLY: Row["discrepancyTally"] = {
  complianceTotal: 0,
  missingEManifest: 0,
  epaCorrection: 0,
  epaCorrectionPreviewLines: [],
  fieldTotal: 0,
  fieldUnresolved: 0,
  fieldPending: 0,
  fieldResolved: 0,
};

/**
 * Flattens one query item into a grid row.
 *
 * The item is spread LAST so its own `id` (the composite `<kind>:<entity id>`
 * the grid keys rows by) wins over the manifest's entity id, and so `manifest`
 * survives for the row-click routing and the drawer hooks, which read the
 * entity id off it.
 *
 * `Row` fields a row with no manifest cannot supply are filled from the item's
 * own equivalents: `manifestTrackingNumber` from `documentNumber` and
 * `shippedDate` from `shipDate`.
 */
export function buildPaperworkRow(item: ShippingPaperworkItem): ShippingPaperworkRow {
  return {
    manifestTrackingNumber: item.documentNumber ?? NO_VALUE_PLACEHOLDER,
    shippedDate: item.shipDate,
    documentKind: item.paperworkKind,
    availableSources: [],
    discrepancyTally: EMPTY_DISCREPANCY_TALLY,
    episodicDeclarable: false,
    ...item.manifest,
    ...item,
    // Two source types are being folded into one row, so neither typename
    // describes it. Dropped rather than left claiming to be one of them.
    __typename: undefined,
  };
}

/**
 * The entity id a drawer or a mutation needs, off the row's populated side.
 * Never the row's own `id`: that is `<kind>:<entity id>`, unique across the two
 * source tables' independent uuid spaces but not a valid id for either.
 */
export function paperworkManifestId(row: Row): string | null {
  return isManifestRow(row) ? row.manifest.id : null;
}

/**
 * Says "No ship date" where the Ship Date column renders nothing, so an
 * incomplete document is visible without opening it. A dated row keeps the
 * column's own `formattedValue`, and the `valueFormatter` stays untouched
 * because the column filter and copy-to-clipboard read it.
 */
export function withMissingShipDateCell(
  columns: OmnisearchGridColDef<Row>[]
): OmnisearchGridColDef<Row>[] {
  return columns.map((column) => {
    if (column.field !== "shippedDate") return column;
    return {
      ...column,
      renderCell: ({ value, formattedValue }: GridRenderCellParams<Row>) =>
        value ? (
          formattedValue
        ) : (
          <Typography variant="body2" color="warning.main">
            {MISSING_SHIP_DATE_LABEL}
          </Typography>
        ),
    };
  });
}

/**
 * Says "No number on bill" where a bill's document number is the generated
 * `NO-NUMBER-` value. Like the Ship Date cell, only `renderCell` changes, so
 * the filter and copy-to-clipboard still read the stored number.
 */
export function withGeneratedNumberCell(
  columns: OmnisearchGridColDef<Row>[]
): OmnisearchGridColDef<Row>[] {
  return columns.map((column) => {
    if (column.field !== "manifestTrackingNumber") return column;
    return {
      ...column,
      renderCell: ({ row, value, formattedValue }: GridRenderCellParams<Row>) =>
        typeof value === "string"
          ? manifestNumberLabel({ manifestTrackingNumber: value })
          : formattedValue,
    };
  });
}

/**
 * Filter and sort keys V2's columns use, mapped to the equivalent column on
 * `ShippingPaperworkView`. The merged view stores one document number and one
 * ship date for both kinds, under names of its own.
 */
export const PAPERWORK_KEY_ALIASES: Record<string, string> = {
  manifestTrackingNumber: "documentNumber",
  shippedDate: "shipDate",
};

/**
 * Columns the merged view can order by. The `shippingPaperwork` resolver
 * accepts a fixed set of view columns and silently falls back to ship date for
 * anything else, so every other V2 column is marked unsortable below rather
 * than left offering a sort arrow that does nothing.
 */
const SORTABLE_FIELDS = new Set(["manifestTrackingNumber", "shippedDate"]);

/**
 * Omnisearch keys the merged view does not register, so a filter on them would
 * match every row instead of narrowing. `OmnisearchManager` skips an unknown
 * key silently, which is exactly the "filter that appears to work and does
 * nothing" this drops instead.
 *
 * `sourceDocumentState` and `hasSource` came off this list once the merged
 * view could answer them through its `processedManifest` relation. Both are
 * manifest concepts, so both narrow the list to manifests.
 */
const UNSUPPORTED_FILTER_FIELDS = new Set(["wasteLineCount"]);

/**
 * Applies the merged view's sort and filter support to V2's columns: no sort
 * arrow on a column the view cannot order by, no filter entry for a key it
 * does not register.
 */
export function withPaperworkQuerySupport(
  columns: OmnisearchGridColDef<Row>[]
): OmnisearchGridColDef<Row>[] {
  return columns.map((column) => ({
    ...column,
    sortable: SORTABLE_FIELDS.has(column.field) && column.sortable !== false,
    filterable: UNSUPPORTED_FILTER_FIELDS.has(column.field) ? false : column.filterable,
  }));
}

/** Grid sort model to the field names the `shippingPaperwork` resolver accepts. */
export function toPaperworkSortModel(sort: GridSortModel) {
  return sort.map((entry) => ({
    field: PAPERWORK_KEY_ALIASES[entry.field] ?? entry.field,
    sort: entry.sort,
  }));
}

/**
 * The merged list's search string in `processedManifests` vocabulary, for the
 * manifest export.
 *
 * The export runs the processed-manifest query, which speaks the manifest
 * table's key names, so the view's names are mapped back. `kind:` is passed
 * through (that query filters on `documentKind`), so the export follows the
 * grid's Type filter.
 */
export function toManifestExportSearch(search: string): string {
  const inverseAliases = new Map(
    Object.entries(PAPERWORK_KEY_ALIASES).map(([manifestKey, viewKey]) => [viewKey, manifestKey])
  );
  return parseOmnisearchTokens(search)
    .filter((token) => token.length > 0)
    .map((token) => {
      const colonIdx = token.indexOf(":");
      if (colonIdx <= 0) return token;
      const key = token.substring(0, colonIdx);
      const manifestKey = inverseAliases.get(key);
      return manifestKey ? `${manifestKey}${token.substring(colonIdx)}` : token;
    })
    .join(" ");
}

const minWidth = 120;

/**
 * The Type column, exported on its own so a test can call
 * `kindColumn.valueGetter` directly against a real row. Read off an array,
 * `valueGetter`'s declared MUI type pins its first parameter to `never`, which
 * no test can supply without an unsound cast.
 */
export const kindColumn = {
  field: "kind",
  headerName: "Type",
  minWidth: 130,
  flex: 0.6,
  sortable: false,
  // A column filter, so the filter panel lists Type in column order.
  filterKeyType: "enumAutocomplete" as const,
  enumValues: shippingPaperworkKindValues(),
  // Labels as the Type cells print them, not "Bill Of Lading".
  enumPresentationFunction: (value: string) => documentKindLabel(value),
  valueGetter: (_: unknown, row: Row) => paperworkTypeLabel(row),
};

/**
 * The shipment a manifest or bill of lading is attached to.
 *
 * Not sortable: the link lives on `WasteShipment`, not on the view this list is
 * built from, so it is resolved per page during hydration rather than being an
 * index column. Not filterable for the same reason.
 *
 * Hidden by default. Most readers come to this list for the paperwork itself,
 * and the link only matters when someone is chasing which shipment a document
 * belongs to.
 */
export function linkedShipmentValue(row: Row): string {
  return row.linkedShipment?.manifestRequestNumber ?? NO_VALUE_PLACEHOLDER;
}

/**
 * Takes the tenant because the cell is a link, and the route is tenant-scoped.
 * Every other cross-reference in this feature is followable: the reconciliation
 * tooltip names the shipment, the rail renders the document number as a link
 * into its drawer. Someone who turns this column on is chasing which shipment a
 * document belongs to, so plain text left them retyping the number into the
 * shipments list.
 */
export function LinkedShipmentCell({ tenantId, row }: { tenantId: string; row: Row }) {
  const shipment = row.linkedShipment;
  if (!shipment) {
    return NO_VALUE_PLACEHOLDER;
  }
  return (
    <Link
      component={RouterLink}
      to={`/o/${tenantId}/waste/shipments/${shipment.id}`}
      // The grid row has its own click handler, which opens the paperwork
      // drawer. Without this, following the link opens both.
      onClick={(event) => event.stopPropagation()}
    >
      {shipment.manifestRequestNumber}
    </Link>
  );
}

export function linkedShipmentColumn(tenantId: string): OmnisearchGridColDef<Row> {
  return {
    field: "linkedShipment",
    headerName: "Linked shipment",
    minWidth,
    flex: 0.8,
    sortable: false,
    filterable: false,
    valueGetter: (_, row) => linkedShipmentValue(row),
    renderCell: ({ row }) => <LinkedShipmentCell tenantId={tenantId} row={row} />,
  };
}

export const INITIAL_COLUMN_VISIBILITY = {
  // Hidden until someone is chasing which shipment a document belongs to.
  linkedShipment: false,
  // Opt-in: only facilities declaring episodic events have anything in it.
  episodic: false,
  hasEpisodic: false,
  // `shippedDate` is deliberately absent: it is the sort key, and the date both
  // kinds are listed by. A bill of lading can be saved without one, and
  // `withMissingShipDateCell` renders the missing-date placeholder rather than an
  // empty cell when that happens.
  // Filter-only, with no cell of its own — V2 forces it hidden in its own
  // visibility model for the same reason.
  hasSource: false,
  isCertified: false,
  sourceDocumentState: false,
  documentSource: false,
  latestEpaCorrectionManifestStatus: false,
  receivedDate: false,
  eManifestDaysInTransit: false,
};

/** V2's mobile view drops the same three columns. */
export const MOBILE_COLUMN_VISIBILITY = {
  ...INITIAL_COLUMN_VISIBILITY,
  epaId: false,
  wasteLineCount: false,
  vendor: false,
};
